import { motion } from "framer-motion";
import { HeartPulse, QrCode, Wrench, Bell, CheckCircle2 } from "lucide-react";

/** Stylised phone frame showcasing the Speed Détresse app.
 *  With `image`: real app screenshot inside the frame. Without: built-in JSX screen. */
export default function PhoneMockup({ image, alt = "Application Speed Détresse", className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${className}`}
    >
      {/* Gold glow behind */}
      <div className="absolute -inset-8 rounded-full bg-gold/15 blur-3xl" />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative mx-auto w-[280px] rounded-[3rem] border border-white/15 bg-navy-elevated p-3 shadow-2xl shadow-black/60 sm:w-[300px]"
        data-testid="phone-mockup"
      >
        <div className="overflow-hidden rounded-[2.4rem] bg-navy">
          {image ? (
            <img src={image} alt={alt} className="h-auto w-full" data-testid="phone-mockup-image" />
          ) : (
          <>
          {/* Notch */}
          <div className="relative flex h-8 items-center justify-center">
            <div className="h-5 w-24 rounded-b-2xl bg-navy-elevated" />
          </div>
          {/* App header */}
          <div className="flex items-center justify-between px-5 pb-3 pt-1">
            <div className="flex items-center gap-2">
              <img src="/logo-s.png" alt="Logo Speed Détresse" className="h-7 w-7 rounded-full object-cover" />
              <span className="font-serif text-sm text-white">Speed <span className="text-gold">Détresse</span></span>
            </div>
            <Bell className="h-4 w-4 text-gold" strokeWidth={1.6} />
          </div>
          {/* Screen content */}
          <div className="space-y-3 px-4 pb-6">
            <p className="px-1 text-[11px] uppercase tracking-[0.2em] text-slate-500">Mon logement · Amiens</p>

            <div className="rounded-2xl border border-white/10 bg-navy-surface p-4">
              <div className="flex items-center gap-2 text-gold"><HeartPulse className="h-4 w-4" strokeWidth={1.6} /><span className="text-xs font-semibold text-white">Carnet de Santé</span></div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "87%" }}
                  transition={{ delay: 1.2, duration: 1.4, ease: "easeOut" }}
                  className="h-full rounded-full bg-gold"
                />
              </div>
              <p className="mt-2 text-[11px] text-slate-400">Suivi à jour · 87 % complété</p>
            </div>

            <div className="rounded-2xl border border-gold/30 bg-navy-surface p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gold"><Wrench className="h-4 w-4" strokeWidth={1.6} /><span className="text-xs font-semibold text-white">Intervention en cours</span></div>
                <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold text-gold">EN DIRECT</span>
              </div>
              <p className="mt-2 text-[11px] text-slate-300">Plombier en route — arrivée estimée 14 h 30</p>
              <div className="mt-3 flex gap-1.5">
                {["Reçue", "Qualifiée", "En route"].map((s) => (
                  <span key={s} className="flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-[9px] text-slate-300">
                    <CheckCircle2 className="h-2.5 w-2.5 text-gold" /> {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-navy-surface p-3 text-center">
                <QrCode className="mx-auto h-8 w-8 text-gold" strokeWidth={1.2} />
                <p className="mt-1.5 text-[10px] text-slate-400">QR logement</p>
              </div>
              <div className="flex flex-col items-center justify-center rounded-2xl bg-gold p-3 text-center">
                <span className="text-[11px] font-bold leading-tight text-navy">Signaler une panne</span>
                <span className="mt-0.5 text-[9px] text-navy/70">en 30 secondes</span>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
